import eventEmitter from "../state/EventEmitter";
import { LoginStatus } from "./LoginStatus";

type API_URL_PARAMS = null | { [key: string]: string | number | boolean };

const DEBUG: boolean = import.meta.env?.VITE_DEBUG === "true";

export { APIclass };
// FIXME: to support cors redirect from /accelerator-api to redirect within /accelerator, a no-cors requeset or a window.location.href redirect should be tried when checking login status
class APIclass {
  private apiUrl: string = "";
  private useCors: boolean = true;
  static ssoRedirectHeader: string = "X-FSS-Redirect";
  static jwtHeader: string = "X-Auth-Token";
  setUrl(apiUrl: string) {
    this.apiUrl = apiUrl;
  }

  public getUrl() {
    return this.apiUrl;
  }

  setCors(enable: boolean) {
    this.useCors = enable;
  }

  authToken: string | null = null;

  async get(
    path: string,
    urlParams: API_URL_PARAMS = null,
    controller: AbortController | null = null,
    json = false,
  ): Promise<Response> {
    return this.httpMethod(
      "GET",
      path,
      urlParams,
      null,
      null,
      json,
      controller,
    );
  }

  async post(
    path: string,
    urlParams: API_URL_PARAMS = null,
    body: any = null,
    formData: FormData | null = null,
    json = true,
  ): Promise<Response> {
    return this.httpMethod("POST", path, urlParams, body, formData, json);
  }

  async put(
    path: string,
    urlParams: API_URL_PARAMS = null,
    body: any = null,
  ): Promise<Response> {
    return this.httpMethod("PUT", path, urlParams, body);
  }

  async patch(
    path: string,
    urlParams: API_URL_PARAMS = null,
    body: any = null,
  ): Promise<Response> {
    return this.httpMethod("PATCH", path, urlParams, body);
  }

  async delete(
    path: string,
    urlParams: API_URL_PARAMS = null,
    body: any = null,
  ): Promise<Response> {
    return this.httpMethod("DELETE", path, urlParams, body);
  }

  async login(creds: { username: string; password: string }): Promise<boolean> {
    try {
      const formData: FormData = new FormData();
      formData.append("username", creds.username);
      formData.append("password", creds.password);
      const resp: Response = await this.httpMethod(
        "POST",
        "/v0/auth/login",
        null,
        null,
        formData,
        false,
      );
      if (resp.ok) {
        // const authToken = await resp.text();
        // console.log('Authenticated with token ' + authToken)
        return true;
      } else {
        return false;
      }
    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async samlRedirectBase(): Promise<string | null> {
    const request = new Request(this.buildUrl("/v0/auth/redirect", null));
    const init = this.buildRequestInit(
      "GET",
      "/v0/auth/redirect",
      null,
      null,
      null,
      false,
    );
    const response: null | string = await window
      .fetch(request, init)
      .then((r) => {
        if (r.ok) {
          return r.text();
        }
        return null;
      })
      .catch((reason: any) => {
        console.log("Fetch failed before completing loginStatus check");
        console.error(reason);
        return null;
      });
    console.log("Saml Redirect Base ", response);
    return response;
  }

  async loginStatus(): Promise<LoginStatus | null> {
    let loginStatus = null;
    try {
      // check samlRedirectBase, see if we should redirct to /v0/auth/user to let browser control auth

      // check auth status
      const request = new Request(this.buildUrl("/v0/auth/user", null));
      const init = this.buildRequestInit(
        "GET",
        "/v0/auth/user",
        null,
        null,
        null,
        false,
      );

      const response: null | Response = await window
        .fetch(request, init)
        .catch((reason: any) => {
          console.log("Fetch failed before completing loginStatus check");
          console.error(reason);
          return null;
        });
      const redirectUrlProm = this.samlRedirectBase();
      if (!response?.ok) {
        const redirectUrl = await redirectUrlProm;
        if (redirectUrl) {
          console.log(
            `Saml Redirect controller found, redirecting to ${redirectUrl}`,
          );
          window.location.href = redirectUrl;
          return null;
        }
      }

      if (response) {
        if (response?.status === 401) {
          // ERROR
          eventEmitter.publish({
            name: "authenticationError",
            val: "",
          });
        }
        loginStatus = (await response.json()) as LoginStatus;
      }
    } catch (e) {
      console.log(e);
      loginStatus = null;
    }
    return loginStatus;
  }

  async logout(): Promise<Response | null> {
    try {
      return await this.httpMethod(
        "POST",
        "/v0/auth/logout",
        null,
        null,
        null,
        false,
      );
    } catch (e) {
      return null;
    }
  }

  /**
   * Designed to implement a Fetch-esque method, with similar return values, using XHRs.
   * Why would you choose this method over the API.httpMethod function ? progress meters mainly
   *
   *
   * @param method
   * @returns String representation of whatever was returned by the controller.
   */
  public xhrHttpGet(
    method: "GET",
    path: string,
    urlParams: API_URL_PARAMS = null,
    controller: AbortController | null = null,
  ): Promise<string> {
    return new Promise((res, rej) => {
      const xhr = new XMLHttpRequest();
      const url: string = this.buildUrl(path, urlParams);
      controller?.signal.addEventListener("abort", () => {
        xhr.abort();
      });
      xhr.addEventListener("readystatechange", () => {
        if (xhr.readyState != xhr.DONE) {
          return;
        }
        if (200 <= xhr.status && xhr.status < 300) {
          res(xhr.responseText);
        } else {
          rej(`Something went wrong with this request.\n${xhr.statusText}`);
        }
      });
      xhr.open(method, url);
      xhr.send();
    });
  }

  public buildRequestInit(
    method: "POST" | "GET" | "PATCH" | "PUT" | "DELETE",
    path: string,
    urlParams: API_URL_PARAMS = null,
    body: any = null,
    formData: FormData | null = null,
    json: boolean = true,
    controller: AbortController | null = null,
  ): RequestInit {
    const init: RequestInit = {
      method: method,
      // mode: this.useCors ? 'cors' : undefined,
      headers: this.buildHeaders(json),
      signal: controller?.signal,
    };
    if (method !== "GET") {
      if (formData != null) {
        init.body = formData;
      } else {
        if (json) {
          init.body = JSON.stringify(body);
        } else {
          init.body = body;
        }
      }
    }
    // DEBUG: if dev
    if (this.useCors !== false && this.useCors !== undefined) {
      init.credentials = DEBUG ? "include" : "same-origin";
    } else {
      init.credentials = "same-origin";
    }
    // } else {
    //     // console.log('Disabled cors, not setting request.credentials')
    // }
    return init;
  }

  public httpMethod(
    method: "POST" | "GET" | "PATCH" | "PUT" | "DELETE",
    path: string,
    urlParams: API_URL_PARAMS = null,
    body: any = null,
    formData: FormData | null = null,
    json: boolean = true,
    controller: AbortController | null = null,
  ): Promise<Response> {
    // build url params
    const request = new Request(this.buildUrl(path, urlParams));
    const init = this.buildRequestInit(
      method,
      path,
      urlParams,
      body,
      formData,
      json,
      controller,
    );
    if (controller?.signal.aborted) {
      throw new Error("AbortedRequest");
    }
    const promise: Promise<Response> = window.fetch(request, init);
    promise.catch((reason: any) => {
      console.log("Fetch failed before completing");
      console.error(reason);
    });
    promise.then((value: Response) => {
      if (value == null) return value;
      if (value.headers.has(APIclass.ssoRedirectHeader)) {
        console.debug("Opaque/SSO redirect encountered");
        //console.debug(`Received header ${APIclass.ssoRedirectHeader}, redirecting to current URL to force application state flush`);
        window.location.href =
          value.headers.get(APIclass.ssoRedirectHeader) ?? value.url;
        return;
      }
      if (value.status === 401) {
        // ERROR
        eventEmitter.publish({
          name: "authenticationError",
          val: "",
        });
      }
      return value;
      // if (value.ok) return value;
      // // throw an error here
      // throw new Error(`Something went wrong with this request.\n${value.statusText}`);
    });
    return promise;
  }

  private buildUrl(path: string, urlParams: API_URL_PARAMS) {
    let url = `${this.apiUrl}${path}${urlParams != null ? "?" : ""}`;

    const keys: string[] = Object.keys(urlParams ?? {});
    for (let i = 0; i < keys.length; i++) {
      const value = urlParams![keys[i]];
      // add key=value&
      url += `${keys[i]}=${value}&`;
    }
    if (keys.length) url = url.substr(0, url.length - 1);

    return url;
  }

  private buildHeaders(json: boolean = false): Record<string, string> {
    const headers: Record<string, string> = {};
    if (json) headers["Content-Type"] = "application/json";
    headers["Accept"] = "application/json";
    // Use JWT if an auth token is provided
    if (this.authToken) {
      headers[APIclass.jwtHeader] = this.authToken;
    }
    return headers;
  }
}

const API = new APIclass();

export default API;
